import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { searchByUsername } from '../../services/api/userService';
import './UserSearchInput.css';

const UserSearchInput = ({ 
  label, 
  placeholder = "Search by username or user ID...", 
  onSelect, 
  selectedUser,
  onClear,
  excludeUserIds = [],
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Calculate dropdown position when results change or window scrolls
  useEffect(() => {
    const updatePosition = () => {
      if (inputRef.current && searchResults.length > 0) {
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [searchResults]);

  // Search users as user types
  useEffect(() => {
    const searchUsers = async () => {
      if (searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        setSearching(true);
        const response = await searchByUsername(searchTerm);
        const users = response.data?.users || [];
        
        // Filter out excluded users
        const filteredUsers = users.filter(user => 
          !excludeUserIds.includes(user.id)
        );
        
        setSearchResults(filteredUsers);
      } catch (err) {
        console.error('Error searching users:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, excludeUserIds]);

  const handleSelectUser = (user) => {
    onSelect(user);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleClear = () => {
    setSearchTerm('');
    setSearchResults([]);
    if (onClear) {
      onClear();
    }
  };

  return (
    <div className="user-search-input">
      {label && <label className="form-label-gaming">{label}</label>}
      
      {selectedUser ? (
        <div className="selected-user-display">
          <div className="user-info">
            <span className="user-name">
              {selectedUser.first_name} {selectedUser.last_name}
            </span>
            {selectedUser.username && <span className="user-username">@{selectedUser.username}</span>}
          </div>
          {!disabled && (
            <button type="button" className="clear-button" onClick={handleClear}>
              ✕
            </button>
          )}
        </div>
      ) : (
        <>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="form-input-gaming"
            autoComplete="off"
          />
          
          {searching && <div className="searching-indicator">Searching...</div>}
          
          {/* Search Results - Using Portal to render at document.body level */}
          {searchResults.length > 0 && ReactDOM.createPortal(
            <div 
              className="search-results-dropdown search-results-portal"
              style={{
                position: 'absolute',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: 99999
              }}
            >
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  className="search-result-item"
                  onClick={() => handleSelectUser(user)}
                >
                  <div className="user-info">
                    <span className="user-name">
                      {user.first_name} {user.last_name}
                    </span>
                    <span className="user-username">@{user.username}</span>
                  </div>
                  {user.organization_name && (
                    <span className="user-org">{user.organization_name}</span>
                  )}
                </div>
              ))}
            </div>,
            document.body
          )}

          {searchTerm.length >= 2 && !searching && searchResults.length === 0 && (
            <div className="no-results">No users found</div>
          )}
        </>
      )}
    </div>
  );
};

export default UserSearchInput;
